<template>
    <Head title="Liste des colis" />

    <TravelLayout :currency="currency">
        <div x-data="handleSelect">
            <Datatables>
                <template #title>
                    Liste des colis
                    <span class="font-medium">({{ packages.total }})</span>
                </template>
                <template #headerButtons>
                    <div class="grid grid-flow-col sm:auto-cols-max sm:justify-end gap-2">
                        <button @click="showSearchModal" title="Rechercher"
                            class="rounded-md py-1 px-2 bg-white text-sky hover:bg-sky-400 hover:text-white"
                        ><i  class="fad fa-search"></i>
                        </button>
                        <a :href="route('travels.packages.export', travel)"
                            title="Exporter en Excel"
                            class="rounded-md py-1 px-2 bg-white text-greendack hover:bg-greendack-400 hover:text-white inline-flex items-center"
                        >
                            <i class="fa fa-file-excel"></i>
                            <span class="text-xs ml-1 hidden sm:inline">Excel</span>
                        </a>
                        <button v-if="$page.props.permissions.canAddPackage" @click.prevent="emitter.emit('showPackageModal')"
                            class="btn bg-white text-sky hover:bg-sky-400 hover:text-white py-1 px-2"
                        >
                            <svg class="h-4 w-4 fill-current shrink-0" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clip-rule="evenodd" />
                            </svg>
                            <span class="text-xs ml-1 hidden sm:inline">Ajouter un colis</span>
                        </button>
                    </div>
                </template>
                <template #tableHeader>
                    <th>#</th>
                    <th>Code</th>
                    <th>Client</th>
                    <th>Destinataire</th>
                    <th>Montant total</th>
                    <th>Reste à payer</th>
                    <th>Statut</th>
                    <th class="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <span class="sr-only">Menu</span>
                    </th>
                </template>
                <template #tableBody>
                    <template v-if="!packages.data.length">
                        <tr>
                            <td colspan="8">
                                <div class="text-center text-2xl text-red-400 bg-red-100 p-10">
                                    Aucun colis enregistré pour le moment.
                                </div>
                            </td>
                        </tr>
                    </template>
                    <template v-else v-for="(pack, index) in packages.data" :key="pack.id">
                        <tr>
                            <td>{{ index + 1 }}</td>
                            <td>
                                <span class="font-bold text-sky">{{ pack.code }}</span>
                            </td>
                            <td>
                                <div class="flex items-center">
                                    <div class="shrink-0 flex items-center justify-center bg-gray-700 rounded-full mr-2">
                                        <img class="w-10 h-10 rounded-full border-2 border-white box-content"
                                            :src="pack.sender.profile_photo_url"
                                        />
                                    </div>
                                    <div class="grow text-left">
                                        <div class="flex items-center gap-2">
                                            <i v-if="$page.props.permissions.canUpdatePackageClient"
                                                @click.prevent="emitter.emit('showChangePackageClientModal', pack)"
                                                class="fad fa-pen text-sm text-sky-500 hover:text-sky-400 cursor-pointer"
                                                title="Modifier le client"
                                            ></i>
                                            <h2 class="font-semibold text-gray-500 inline-flex">{{ pack.sender_real_name }}</h2>
                                        </div>
                                        <div class="text-xs text-gray-400 font-semibold">{{ pack.sender_real_phone }}</div>
                                    </div>
                                </div>
                            </td>
                            <td>
                                <div class="grow text-left">
                                    <h2 class="font-semibold text-gray-500 inline-flex">{{ pack.receiver_name ?? 'Non renseigné' }}</h2>
                                    <div :class="'text-xs text-gray-400'+ (pack.receiver_phone ? ' font-semibold' : '')">{{ pack.receiver_phone ? '+' + pack.receiver_dial_code + '' + pack.receiver_phone : 'Non renseigné' }}</div>
                                </div>
                            </td>
                            <td>
                                <div class="text-right font-bold"
                                    :class="pack.invoice.balance > 0 ? 'text-gray-500' : 'text-greendack-500'"
                                >
                                    {{ formatAmount(pack.invoice.amount, currency) }}
                                    <div v-if="Number(pack.discount_percent) > 0" class="text-xs font-semibold text-red-500">
                                        −{{ pack.discount_percent }}%
                                    </div>
                                </div>
                            </td>
                            <td>
                                <div class="text-right font-bold"
                                    :class="pack.invoice.balance > 0 ? 'text-red-500' : 'text-greendack-500'"
                                >{{ formatAmount(pack.invoice.balance, currency) }}</div>
                            </td>
                            <td><StatusBadge :label="pack.status_text" :color="pack.status_color" /></td>
                            <td class="whitespace-nowrap w-px">
                                <div class="space-x-5 flex items-center justify-end">
                                    <i @click.prevent="openDetails(pack)" aria-controls="description-01"
                                        class="fad fa-arrow-down text-sm text-sky hover:text-sky-500 transform
                                        transition-transform duration-200 ease-in-out cursor-pointer"
                                        :class="{ 'rotate-180' : selectedPackage && selectedPackage.id === pack.id }"
                                        :title="selectedPackage && selectedPackage.id === pack.id ? 'Cacher' : 'Détails'"
                                    ></i>

                                    <i v-if="$page.props.permissions.canUpdatePackage"
                                        @click.prevent="emitter.emit('showPackageModal', pack)"
                                        class="fad fa-pencil text-sm text-sky-500 hover:text-sky-400 cursor-pointer"
                                        title="Modifier"
                                    ></i>
                                    <i v-else class="fad fa-pencil text-sm text-gray-400 opacity-50 cursor-not-allowed"
                                        title="Impossible de modifier"
                                    ></i>

                                    <i @click.prevent="printForCustomer(pack)"
                                        class="fad fa-print text-sm text-greendack hover:text-greendack-400 cursor-pointer"
                                        title="Imprimer le reçu"
                                    ></i>

                                    <i v-if="!pack.invoice.payments.length && $page.props.permissions.canDeletePackage"
                                        @click.prevent="destroyPackage(pack)"
                                        class="fad fa-trash text-sm text-red-500 hover:text-red-400 cursor-pointer"
                                        title="Supprimer"
                                    ></i>
                                    <i v-else class="fad fa-trash text-sm text-gray-400 opacity-50 cursor-not-allowed"
                                        title="Impossible de supprimer"
                                    ></i>

                                    <i v-if="$page.props.permissions.canBeMigratePackage"
                                        @click.prevent="emitter.emit('showMigratePackageModal', pack)"
                                        class="fad fa-exchange text-sm text-violet hover:text-violet-500 cursor-pointer"
                                        title="Transférer"
                                    ></i>
                                    <i v-else class="fad fa-exchange text-sm text-gray-400 opacity-50 cursor-not-allowed"
                                        title="Impossible de transférer"
                                    ></i>
                                </div>
                            </td>
                        </tr>
                        <tr id="description-01" role="region" v-show="selectedPackage && selectedPackage.id === pack.id">
                            <td colspan="8">
                                <PackageDetails :pack="pack" :modes="modes" :currency="currency" :travel="travel" />
                            </td>
                        </tr>
                    </template>
                </template>
            </Datatables>
        </div>

        <Pagination :links="packages.links" />

        <PackageForm
            v-if="$page.props.permissions.canUpdatePackage || $page.props.permissions.canAddPackage"
            :modes="modes"
            :travel="travel"
            :currency="currency"
        />

        <MigratePackageForm
            v-if="$page.props.permissions.canBeMigratePackage"
            :travel="travel"
        />

        <ChangePackageClientForm
            v-if="$page.props.permissions.canUpdatePackageClient"
            :travel="travel"
        />

        <JetDialogModal :show="searchModal" @close="closeSearchModal" max-width="xl">
            <template #title>Rechercher un colis</template>

            <template #content>
                <GlobalFilter @search-launched="closeSearchModal" />
            </template>
        </JetDialogModal>
    </TravelLayout>
</template>

<script>
    import { defineComponent } from 'vue'
    import useDevice from '@/composables/device'
    import { Head } from '@inertiajs/inertia-vue3';
    import useDropdown from '@/composables/dropDown'
    import Pagination from '@/Components/Pagination'
    import Datatables from '@/Components/Datatables'
    import PackageForm from '@/Components/PackageForm'
    import PrintMixins from '@/Mixins/PrintMixins.vue'
    import StatusBadge from '@/Components/StatusBadge'
    import JetDialogModal from '@/Jetstream/DialogModal'
    import GlobalFilter from '@/Components/GlobalFilter'
    import CurrencyMixins from '@/Mixins/CurrencyMixins'
    import TravelLayout from '@/Layouts/TravelLayout.vue'
    import PackageDetails from '@/Components/PackageDetails'
    import SearchActionMixins from '@/Mixins/SearchActionMixins'
    import MigratePackageForm from '@/Components/MigratePackageForm'
    import ChangePackageClientForm from '@/Components/ChangePackageClientForm'

    export default defineComponent({
        components: {
            Head,
            Pagination,
            Datatables,
            PackageForm,
            StatusBadge,
            TravelLayout,
            GlobalFilter,
            PackageDetails,
            JetDialogModal,
            MigratePackageForm,
            ChangePackageClientForm
        },

        mixins: [PrintMixins, CurrencyMixins, SearchActionMixins],

        props: {
            modes: Array,
            travel: Object,
            packages: Object,
            currency: Object
        },

        setup() {
            const { isMobile, isTablet, isDesktop } = useDevice()

            const { dropdownOpen, trigger, dropdown } = useDropdown()

            return {
                isMobile,
                isTablet,
                isDesktop,

                dropdownOpen,
                trigger,
                dropdown
            }
        },

        data() {
            return {
                selectedPackage: null,
                manageModal: false
            }
        },

        methods: {
            openDetails(pack) {
                if (this.selectedPackage && this.selectedPackage.id === pack.id) {
                    this.selectedPackage = null;
                } else {
                    this.selectedPackage = pack
                }
            },

            printForCustomer(pack) {
                const url = route('travels.packages.printForCustomer', {
                    travel: this.travel,
                    package: pack
                })

                if (this.isMobile || this.isTablet) {
                    // 📱 MOBILE → PREVIEW
                    const link = document.createElement('a')
                    link.href = url
                    // link.target = '_blank'
                    link.click()
                } else {
                    // 💻 DESKTOP → NEW TAB
                    this.print(url)
                }
            },

            destroyPackage(pack) {
                this.$swal.queue([{
                    title: "Colis",
                    text: "Etes vous sûr de vouloir supprimer le colis ?",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: 'rgb(241, 162, 0)',
                    cancelButtonColor: '#bd081c',
                    confirmButtonText: "Supprimer",
                    showLoaderOnConfirm: true,
                    preConfirm: () => {
                        return this.$inertia.delete(route('travels.packages.destroy', {travel: this.travel, 'package': pack}), {
                            preserveScroll: true,
                            preserveState: true,
                            onSuccess: () => {
                                this.$swal.fire(
                                    'Colis',
                                    'Colis supprimé avec succès.',
                                    'success'
                                )
                            },
                            onError: (error) => {
                                this.$swal.fire({
                                    icon: 'error',
                                    title: error.message
                                })
                            }
                        })
                    }
                }])
            }
        }
    })
</script>